import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AppContext } from '../App';
import { X, Users, Clock, DollarSign, Briefcase, MapPin, User, Percent } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from './ui/sheet';

// Smart number formatting - "h" suffix added separately for hours
const formatNumber = (num, prefix = '') => {
  if (num === null || num === undefined) return prefix + '0';
  const absNum = Math.abs(num);
  if (absNum >= 1000000) {
    return prefix + (num / 1000000).toFixed(2) + 'M';
  } else if (absNum >= 100000) {
    return prefix + (num / 1000).toFixed(0) + 'k';
  } else if (absNum >= 10000) {
    return prefix + (num / 1000).toFixed(1) + 'k';
  } else {
    return prefix + num.toLocaleString();
  }
};

const EmployeeDrillDown = ({ isOpen, onClose, filterType, filterValue, payType = null, additionalFilters = {} }) => {
  const { API } = React.useContext(AppContext);
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    if (isOpen && filterValue) {
      fetchEmployeeData();
    }
  }, [isOpen, filterType, filterValue, payType, additionalFilters]);

  const fetchEmployeeData = async () => {
    setLoading(true);
    try {
      // Start with additional filters (date range, location, department, manager from dashboard)
      const filters = { ...additionalFilters };
      
      // Add the drill-down specific filter
      if (filterType && filterValue && filterValue !== 'all') {
        filters[filterType] = filterValue;
      }
      
      // Add pay type filter if specified (single)
      if (payType) {
        filters.pay_type = payType;
      }
      
      // Handle pay_types array from global filters
      if (additionalFilters.pay_types && additionalFilters.pay_types.length > 0) {
        filters.pay_types = additionalFilters.pay_types;
      }
      
      // Remove null/undefined values
      Object.keys(filters).forEach(key => {
        if (filters[key] === null || filters[key] === undefined || filters[key] === '') {
          delete filters[key];
        }
      });
      
      const response = await axios.post(`${API}/employees/overtime`, filters);
      setEmployees(response.data.employees || []);
      setSummary({
        total_employees: response.data.total_employees,
        total_premium_hours: response.data.total_premium_hours || response.data.total_overtime_hours,
        total_non_premium_hours: response.data.total_non_premium_hours || 0,
        total_hours: response.data.total_hours || 0,
        premium_percent: response.data.premium_percent || 0,
        total_premium_cost: response.data.total_overtime_cost,
        total_non_premium_cost: response.data.total_non_premium_cost || 0
      });
    } catch (error) {
      console.error('Error fetching employee data:', error);
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  const getFilterLabel = () => {
    const parts = [];
    
    // Add drill-down filter
    if (filterType && filterValue) {
      const labels = {
        department: 'Department',
        location: 'Location',
        manager: 'Manager',
        supervisor_l1: 'Direct Supervisor',
        supervisor_l2: 'Level 2 Supervisor',
        supervisor_l3: 'Level 3 Supervisor',
        job_title: 'Job Title'
      };
      parts.push(`${labels[filterType] || filterType}: ${filterValue}`);
    }
    
    // Add pay type filter if present
    if (payType) {
      parts.push(`Pay Type: ${payType}`);
    }
    
    // Add pay_types if present
    if (additionalFilters.pay_types && additionalFilters.pay_types.length > 0) {
      parts.push(`Pay Types: ${additionalFilters.pay_types.join(', ')}`);
    }
    
    // Add date range if present
    if (additionalFilters.start_date && additionalFilters.end_date) {
      parts.push(`${additionalFilters.start_date} to ${additionalFilters.end_date}`);
    }
    
    // Add other active filters
    if (additionalFilters.location && filterType !== 'location') {
      parts.push(`Location: ${additionalFilters.location}`);
    }
    if (additionalFilters.department && filterType !== 'department') {
      parts.push(`Dept: ${additionalFilters.department}`);
    }
    if (additionalFilters.manager && filterType !== 'manager') {
      parts.push(`Manager: ${additionalFilters.manager}`);
    }
    
    return parts.length > 0 ? parts.join(' | ') : 'All Employees';
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" data-testid="employee-drilldown-sheet">
        <SheetHeader className="mb-6">
          <SheetTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Employee Overtime Details
          </SheetTitle>
          <SheetDescription>
            {getFilterLabel()}
          </SheetDescription>
        </SheetHeader>

        {loading ? (
          <div className="flex items-center justify-center h-64" data-testid="drilldown-loading">
            <div className="text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto"></div>
              <p className="mt-3 text-muted-foreground text-sm">Loading employee data...</p>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Summary Cards - Enhanced with non-premium */}
            {summary && (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="drilldown-summary">
                <div className="bg-slate-50 rounded-lg p-3 text-center">
                  <Users className="h-5 w-5 mx-auto mb-1 text-blue-600" />
                  <div className="text-lg font-bold text-foreground">{summary.total_employees}</div>
                  <div className="text-xs text-muted-foreground">Employees</div>
                </div>
                <div className="bg-orange-50 rounded-lg p-3 text-center">
                  <Clock className="h-5 w-5 mx-auto mb-1 text-orange-600" />
                  <div className="text-lg font-bold text-orange-600">{formatNumber(summary.total_premium_hours)}</div>
                  <div className="text-xs text-muted-foreground">Premium Hours</div>
                </div>
                <div className="bg-slate-50 rounded-lg p-3 text-center">
                  <Clock className="h-5 w-5 mx-auto mb-1 text-slate-600" />
                  <div className="text-lg font-bold text-slate-600">{formatNumber(summary.total_non_premium_hours)}</div>
                  <div className="text-xs text-muted-foreground">Non-Premium Hours</div>
                </div>
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <Percent className="h-5 w-5 mx-auto mb-1 text-green-600" />
                  <div className="text-lg font-bold text-green-600">{summary.premium_percent?.toFixed(1)}%</div>
                  <div className="text-xs text-muted-foreground">Premium %</div>
                </div>
              </div>
            )}
            
            {/* Cost Summary */}
            {summary && (
              <div className="grid grid-cols-2 gap-3" data-testid="drilldown-cost-summary">
                <div className="bg-red-50 rounded-lg p-3 text-center">
                  <DollarSign className="h-5 w-5 mx-auto mb-1 text-red-600" />
                  <div className="text-lg font-bold text-red-600">{formatNumber(summary.total_premium_cost, '$')}</div>
                  <div className="text-xs text-muted-foreground">Premium Cost</div>
                </div>
                <div className="bg-slate-50 rounded-lg p-3 text-center">
                  <DollarSign className="h-5 w-5 mx-auto mb-1 text-slate-600" />
                  <div className="text-lg font-bold text-slate-600">{formatNumber(summary.total_non_premium_cost, '$')}</div>
                  <div className="text-xs text-muted-foreground">Non-Premium Cost</div>
                </div>
              </div>
            )}

            {/* Employee List - Redesigned Cards */}
            <div className="space-y-4" data-testid="employee-list">
              {employees.length > 0 && (
                <div className="flex items-center gap-2 text-xs text-[#2E5C8A] bg-[#2E5C8A]/5 px-3 py-2 rounded-lg" data-testid="employee-click-hint">
                  <User className="h-3.5 w-3.5 flex-shrink-0" />
                  <span>Click any employee name to see their full profile</span>
                </div>
              )}
              {employees.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="h-12 w-12 mx-auto mb-3 opacity-20" />
                  <p>No employees with overtime in this selection</p>
                </div>
              ) : (
                employees.map((employee, idx) => (
                  <motion.div
                    key={employee.employee_id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    className="bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-lg transition-shadow"
                    data-testid={`employee-card-${employee.employee_id}`}
                  >
                    {/* Card Header - Name & Premium Hours */}
                    <div className="bg-gradient-to-r from-slate-50 to-white px-4 py-3 border-b border-slate-100">
                      <div className="flex justify-between items-start">
                        <div>
                          <button
                            onClick={() => { onClose(); navigate(`/employee/${employee.employee_id}`); }}
                            className="font-semibold text-blue-600 text-base hover:text-blue-800 hover:underline transition-colors cursor-pointer"
                            data-testid={`employee-link-${employee.employee_id}`}
                          >
                            {employee.employee_name}
                          </button>
                          <p className="text-sm text-muted-foreground flex items-center gap-1 mt-0.5">
                            <Briefcase className="h-3 w-3" />
                            {employee.job_title}
                          </p>
                        </div>
                        <div className="text-right">
                          <div className="inline-flex items-center gap-1.5 bg-orange-100 text-orange-700 px-3 py-1.5 rounded-full">
                            <Clock className="h-4 w-4" />
                            <span className="text-lg font-bold">{formatNumber(employee.total_premium_hours || employee.overtime_hours)}</span>
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            Premium Hours 
                            <span className={`ml-1 px-1.5 py-0.5 rounded text-xs font-semibold ${
                              (employee.premium_percent || 0) > 20 ? 'bg-red-100 text-red-700' :
                              (employee.premium_percent || 0) > 15 ? 'bg-orange-100 text-orange-700' :
                              'bg-green-100 text-green-700'
                            }`}>
                              {(employee.premium_percent || 0).toFixed(1)}%
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Location & Manager Row */}
                    <div className="px-4 py-2 bg-slate-50/50 flex items-center gap-4 text-sm border-b border-slate-100">
                      <div className="flex items-center gap-1.5 text-slate-600">
                        <MapPin className="h-3.5 w-3.5 text-slate-400" />
                        <span>{employee.location}</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-slate-600">
                        <User className="h-3.5 w-3.5 text-slate-400" />
                        <span>{employee.manager}</span>
                      </div>
                    </div>

                    {/* Hours Breakdown - 3 Column Layout */}
                    <div className="px-4 py-3">
                      <div className="grid grid-cols-3 gap-3">
                        {/* Premium */}
                        <div className="bg-orange-50 rounded-lg p-3 text-center">
                          <div className="text-xs font-medium text-orange-600 uppercase tracking-wide mb-1">Premium</div>
                          <div className="text-lg font-bold text-orange-700">{formatNumber(employee.total_premium_hours || employee.overtime_hours)}</div>
                          <div className="text-sm font-medium text-orange-600">{formatNumber(employee.overtime_cost, '$')}</div>
                          <div className="text-xs text-orange-500 mt-1">{(employee.premium_percent || 0).toFixed(1)}% of total</div>
                        </div>
                        
                        {/* Non-Premium */}
                        <div className="bg-slate-50 rounded-lg p-3 text-center">
                          <div className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Non-Premium</div>
                          <div className="text-lg font-bold text-slate-700">{formatNumber(employee.non_premium_hours || 0)}</div>
                          <div className="text-sm font-medium text-slate-500">{formatNumber(employee.non_premium_cost || 0, '$')}</div>
                        </div>
                        
                        {/* Total */}
                        <div className="bg-blue-50 rounded-lg p-3 text-center">
                          <div className="text-xs font-medium text-blue-600 uppercase tracking-wide mb-1">Total</div>
                          <div className="text-lg font-bold text-blue-700">{formatNumber(employee.total_hours || 0)}</div>
                          <div className="text-sm font-medium text-blue-600">{formatNumber((employee.overtime_cost || 0) + (employee.non_premium_cost || 0), '$')}</div>
                        </div>
                      </div>
                    </div>

                    {/* Footer Stats */}
                    <div className="px-4 py-2 bg-slate-50 border-t border-slate-100">
                      <div className="flex items-center justify-between text-xs text-slate-600">
                        <span><strong>{employee.weeks_worked}</strong> weeks worked</span>
                        <span className="text-slate-300">•</span>
                        <span>Avg <strong className="text-orange-600">{employee.avg_weekly_premium || employee.avg_weekly_overtime}h</strong>/week premium</span>
                        <span className="text-slate-300">•</span>
                        <span>Rate: <strong>${employee.hourly_rate}</strong>/hr</span>
                      </div>
                    </div>
                  </motion.div>
                ))
              )}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default EmployeeDrillDown;
